// src/api/client.ts
// Central Axios instance. All API calls go through this client.
// Rule: never import axios directly in components or stores — always use this.

import axios from 'axios'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'
import { router } from '@/router'

// Queue to hold pending requests while the token is refreshing
let isRefreshing = false
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: unknown) => void }> = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 30000, // Enterprise standard: 30s timeout to prevent hanging requests
  withCredentials: true,
  xsrfCookieName: 'csrf_token',
  xsrfHeaderName: 'X-CSRF-Token',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Helper to read cookie
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? match[2] : null
}

// ── Request interceptor — attach access token & CSRF ─────────────────
apiClient.interceptors.request.use((config) => {
  const authStore = useAuthStore()
  if (authStore.accessToken) {
    config.headers.Authorization = `Bearer ${authStore.accessToken}`
  }
  
  // Explicitly attach CSRF token for cross-origin backend requests
  const csrf = getCookie('csrf_token')
  if (csrf) {
    config.headers['X-CSRF-Token'] = csrf
  }
  
  return config
})

// ── Response interceptor — auto-refresh & global error handling ─
apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config
    const ui = useUIStore()

    // 1. Handle Network & Timeout Errors
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        ui.error('Koneksi Terputus', 'Server terlalu lama merespon. Silakan coba lagi.')
      } else {
        ui.error('Kesalahan Jaringan', 'Gagal terhubung ke server. Periksa koneksi internet Anda.')
      }
      return Promise.reject(error)
    }

    const { status, data } = error.response

    // 2. Auto-refresh on 401
    const isAuthEndpoint =
      originalRequest?.url?.includes('/auth/refresh') ||
      originalRequest?.url?.includes('/auth/login') ||
      originalRequest?.url?.includes('/auth/logout') ||
      originalRequest?.url?.includes('/auth/register') ||
      originalRequest?.url?.includes('/auth/verify-email') ||
      originalRequest?.url?.includes('/auth/forgot-password') ||
      originalRequest?.url?.includes('/auth/reset-password') ||
      originalRequest?.url?.includes('/auth/resend-verification')

    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return apiClient(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      const authStore = useAuthStore()
      try {
        await authStore.refreshToken()
        processQueue(null, authStore.accessToken)
        originalRequest.headers.Authorization = `Bearer ${authStore.accessToken}`
        return apiClient(originalRequest)
      } catch (err) {
        processQueue(err, null)
        await authStore.logout().catch(() => {})
        // Don't redirect from public routes — unauthenticated users may be browsing
        // job listings or about pages that optionally call protected endpoints.
        if (router.currentRoute.value.meta?.public !== true) {
          router.push('/login')
        }
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    // 3. Global UI Feedback for Specific Status Codes
    if (status === 429) {
      ui.error('Terlalu Banyak Permintaan', 'Silakan tunggu beberapa saat sebelum mencoba kembali.')
    } else if (status >= 500) {
      ui.error('Masalah Server', 'Terjadi kesalahan pada sistem kami. Tim teknis telah dinotifikasi.')
    } else if (status === 403 && !isAuthEndpoint) {
      if (['COMPANY_ACCOUNT_DEACTIVATED', 'COMPANY_ACCESS_DENIED', 'COMPANY_MISMATCH'].includes(data?.error?.code)) {
        ui.error('Sesi Berakhir', data.error.message || 'Akses perusahaan Anda telah dicabut.')
        const authStore = useAuthStore()
        await authStore.logout().catch(() => {})
        if (router.currentRoute.value.meta?.public !== true) {
          router.push('/login')
        }
      } else {
        ui.error('Akses Ditolak', data?.error?.message || 'Anda tidak memiliki izin untuk melakukan aksi ini.')
      }
    }

    return Promise.reject(error)
  },
)
